import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Archive, RotateCcw, Loader2, GraduationCap, Users, ChevronRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TeacherShell } from "@/components/TeacherShell";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function ArchivedClasses() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/classes/archived");
      setClasses(data.classes);
    } catch {
      toast.error("Chargement des classes archivées impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await api.post(`/classes/${restoreTarget.id}/unarchive`);
      toast.success("Classe restaurée. Elle réapparaît sur votre tableau de bord.");
      setRestoreTarget(null);
      load();
    } catch {
      toast.error("Restauration impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <TeacherShell active="archives">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-slate-100">Classes archivées</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Vos classes archivées sont conservées avec toutes leurs données. Consultez-les ou restaurez-les à tout moment.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : classes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-slate-200 py-20 text-center dark:border-white/10" data-testid="archived-empty">
          <Archive className="text-slate-300" size={40} strokeWidth={1} />
          <p className="text-sm text-slate-500">Aucune classe archivée pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="archived-grid">
          {classes.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="flex flex-col gap-4 rounded-2xl border border-slate-200/70 bg-card p-6 dark:border-white/10"
              data-testid={`archived-class-${c.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-white/10"><GraduationCap size={20} /></span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">{c.name}</h3>
                    {c.level && <span className="text-xs font-medium text-slate-400">{c.level}</span>}
                  </div>
                </div>
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">Archivée</span>
              </div>

              <div className="flex flex-col gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5"><Users size={15} /> {c.student_count} élève{c.student_count > 1 ? "s" : ""}</span>
                {c.school_year && <span className="flex items-center gap-1.5"><Calendar size={15} /> Année {c.school_year}</span>}
                <span className="text-xs text-slate-400">Archivée le {fmtDate(c.archived_at)}</span>
              </div>

              <div className="mt-1 flex gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => navigate(`/enseignant/classe/${c.id}`)} data-testid={`open-archived-${c.id}`}>
                  Consulter <ChevronRight size={15} className="ml-1" />
                </Button>
                <Button variant="outline" className="rounded-xl" onClick={() => setRestoreTarget(c)} data-testid={`restore-archived-${c.id}`}>
                  <RotateCcw size={15} className="mr-1.5" /> Restaurer
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!restoreTarget} onOpenChange={(v) => !v && setRestoreTarget(null)}>
        <DialogContent className="rounded-2xl sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Restaurer « {restoreTarget?.name} » ?</DialogTitle>
            <DialogDescription>
              La classe réapparaîtra parmi vos classes actives sur le tableau de bord et redeviendra modifiable.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRestoreTarget(null)}>Annuler</Button>
            <Button className="rounded-xl bg-blue-900 text-white hover:bg-blue-800 dark:bg-blue-500" onClick={restore} disabled={busy} data-testid="confirm-restore-class">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Restaurer la classe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TeacherShell>
  );
}
